import { useEffect, useRef, useState } from "react";
import { fetchModulesList, DataReceiverError } from "../../utils/dataReceiver";

/**
 * Hook for the Building in Production modal.
 *
 * Fetches the modules list and derives property-level totals.
 * Handles loading, error, and empty states.
 *
 * @returns {{
 *   modules: Array<{
 *     BatchNum: string,
 *     Property: string,
 *     scheduledOnlineDate: string,
 *     WoNum: string,
 *     ItemNum: string,
 *     Sqft: number,
 *     actualOnlineDate: string,
 *   }>,
 *   propertyTotals: Array<{
 *     property: string,
 *     totalSqft: number,
 *   }>,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export function useBuildingProduction() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // hasFetched ref prevents React 18 Strict Mode's double-invoke from firing
  // two requests. No cleanup / AbortController - see onlineMods.js for the
  // detailed explanation of why any cleanup here causes stuck loading state.
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const rows = await fetchModulesList();
        setModules(rows);
      } catch (err) {
        setError(
          err instanceof DataReceiverError
            ? err.message
            : "Unexpected error loading production data.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────────
  // Aggregate total SQFT per property, preserving insertion order so the
  // property table is stable across renders.
  const propertyTotals = Object.values(
    modules.reduce((acc, mod) => {
      const key = mod.Property;
      if (!acc[key]) acc[key] = { property: key, totalSqft: 0 };
      acc[key].totalSqft += Number(mod.Sqft) || 0;
      return acc;
    }, {}),
  );

  return {
    modules,
    propertyTotals,
    loading,
    error,
  };
}
